import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import type { WizardState, WizardVerticalLabels } from '@lipaflow/shared'
import {
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  UserPlusIcon,
  CloudArrowUpIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import CreateTenantStep from './steps/CreateTenantStep'
import VerifyTenantStep from './steps/VerifyTenantStep'
import SetupSubscriptionStep from './steps/SetupSubscriptionStep'
import SetupWhatsAppStep from './steps/SetupWhatsAppStep'
import CreateUserStep from './steps/CreateUserStep'
import ImportDataStep from './steps/ImportDataStep'
import CompleteStep from './steps/CompleteStep'
import { adminService } from '../../services/admin'
import { sectorPath, useSector, useSectors } from '../../sector/sector'

const WIZARD_STEPS = [
  { key: 'admin_create_institution', label: 'Create Customer', icon: BuildingOfficeIcon },
  { key: 'admin_verify_institution', label: 'Verify', icon: ShieldCheckIcon },
  { key: 'admin_setup_subscription', label: 'Trial', icon: CreditCardIcon },
  { key: 'admin_setup_whatsapp', label: 'WhatsApp', icon: ChatBubbleLeftRightIcon },
  { key: 'admin_create_user', label: 'Admin User', icon: UserPlusIcon },
  { key: 'admin_import_data', label: 'Import', icon: CloudArrowUpIcon },
  { key: 'admin_complete', label: 'Complete', icon: RocketLaunchIcon },
]

const EMPTY_LABELS: WizardVerticalLabels = {}

/** The wizard is part of Onboarding, not a separate flow: always a way back to the list. */
function BackToOnboarding() {
  const sector = useSector()
  return (
    <Link to={sectorPath(sector.code, 'onboarding')} className="mb-4 inline-flex items-center gap-1 text-body-sm text-ink-muted hover:text-ink">
      ← Onboarding
    </Link>
  )
}

/** A customer reached under another sector's address (an old or shared link). */
function OtherSector({ code, tenantId }: { code: string; tenantId: string }) {
  const sector = useSector()
  const { data: sectors = [] } = useSectors()
  const theirs = sectors.find((s) => s.code === code)
  return (
    <div className="max-w-4xl mx-auto rounded-card border border-hairline bg-surface p-8 text-center space-y-4">
      <p className="font-inter text-body text-ink">
        This customer is in {theirs?.name ?? code}, not {sector.name}.
      </p>
      <Link to={sectorPath(code, `onboarding/${tenantId}`)} className="btn-primary inline-flex">
        Open in {theirs?.name ?? code}
      </Link>
    </div>
  )
}

export default function AdminWizardPage() {
  const navigate = useNavigate()
  const sector = useSector()
  const toCustomer = (id: string) => navigate(sectorPath(sector.code, `onboarding/${id}`))
  const { tenantId } = useParams<{ tenantId: string }>()
  const isNewWizard = !tenantId || tenantId === 'new'
  const queryClient = useQueryClient()
  const [rewindStep, setRewindStep] = useState('')

  const { data: wizardState, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-wizard', tenantId],
    queryFn: async () => {
      const res = await api.get<WizardState>(`/admin/wizard/state/${tenantId}`)
      return res.data
    },
    enabled: !!tenantId && !isNewWizard,
  })

  const verticalLabels = wizardState?.vertical_labels || EMPTY_LABELS

  const rewindOptions = useMemo(() => {
    if (!wizardState) return []
    return WIZARD_STEPS.filter(
      (step) =>
        // Only a step already done can be reopened: the current one is open.
        step.key !== 'admin_create_institution' &&
        step.key !== wizardState.current_step &&
        wizardState.completed_steps.includes(step.key)
    )
  }, [wizardState])

  useEffect(() => {
    if (!rewindOptions.length) {
      setRewindStep('')
      return
    }
    if (!rewindOptions.some((step) => step.key === rewindStep)) {
      setRewindStep(rewindOptions[0].key)
    }
  }, [rewindOptions, rewindStep])

  const rewindMutation = useMutation({
    mutationFn: async (step: string) => {
      if (!tenantId) throw new Error('Missing customer ID')
      return adminService.rewindWizardStep(tenantId, step, 'Manual correction from admin wizard')
    },
    onSuccess: (_data, step) => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      const label = WIZARD_STEPS.find((s) => s.key === step)?.label ?? step
      toast.success(`Reopened step: ${label}`)
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to reopen step'))
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    )
  }

  if (isNewWizard) {
    return (
      <div className="max-w-4xl mx-auto">
        <BackToOnboarding />
        <CreateTenantStep onSuccess={toCustomer} />
      </div>
    )
  }

  // An existing customer's setup that didn't load is an error, never the
  // "create a customer" form (that would invite a duplicate).
  if (isError || !wizardState) {
    return (
      <div className="max-w-4xl mx-auto">
        <BackToOnboarding />
        <div role="alert" className="rounded-card border border-hairline bg-surface p-6 space-y-3">
          <h2 className="font-outfit text-h4 text-ink">Couldn't load this customer's setup</h2>
          <p className="text-body-sm text-ink-muted">{apiErrorMessage(error, 'Check your connection and try again.')}</p>
          <button type="button" onClick={() => refetch()} className="btn-primary">Try again</button>
        </div>
      </div>
    )
  }

  if (wizardState.vertical_code && wizardState.vertical_code !== sector.code) {
    return <OtherSector code={wizardState.vertical_code} tenantId={tenantId!} />
  }

  const renderCurrentStep = () => {
    const stepProps = { tenantId: tenantId!, verticalLabels }

    switch (wizardState?.current_step) {
      case 'admin_verify_institution':
        return <VerifyTenantStep {...stepProps} />
      case 'admin_setup_subscription':
        return <SetupSubscriptionStep {...stepProps} />
      case 'admin_setup_whatsapp':
        return <SetupWhatsAppStep {...stepProps} />
      case 'admin_create_user':
        return <CreateUserStep {...stepProps} />
      case 'admin_import_data':
        return <ImportDataStep {...stepProps} />
      case 'admin_complete':
        return <CompleteStep {...stepProps} blockers={wizardState.go_live_blockers ?? []} />
      default:
        return <CreateTenantStep onSuccess={toCustomer} />
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <BackToOnboarding />
      {/* Wizard Controls */}
      {!!rewindOptions.length && (
        <div className="mb-4 rounded-button border border-hairline bg-surface-alt px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-inter text-body-sm font-medium text-ink">Need to edit a previous step?</p>
              <p className="font-inter text-caption text-ink-muted">
                Reopen a completed step and continue the flow from there.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={rewindStep}
                onChange={(e) => setRewindStep(e.target.value)}
                className="rounded-button border border-hairline bg-surface px-3 py-2 text-sm text-ink"
              >
                {rewindOptions.map((step) => (
                  <option key={step.key} value={step.key}>
                    {step.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  if (!rewindStep) return
                  rewindMutation.mutate(rewindStep)
                }}
                disabled={!rewindStep || rewindMutation.isPending}
                className="rounded-button bg-accent px-3 py-2 text-sm font-medium text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
              >
                {rewindMutation.isPending ? 'Reopening…' : 'Reopen this step'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {WIZARD_STEPS.map((step, index) => {
            const isCompleted = wizardState?.completed_steps.includes(step.key)
            const isCurrent = wizardState?.current_step === step.key
            const Icon = step.icon

            return (
              <div key={step.key} className="flex items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    isCompleted
                      ? 'bg-accent text-on-accent'
                      : isCurrent
                      ? 'bg-accent-soft text-accent border-2 border-accent'
                      : 'bg-surface-alt text-ink-faint'
                  }`}
                >
                  {isCompleted ? <CheckCircleIcon className="w-6 h-6" /> : <Icon className="w-5 h-5" />}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    isCurrent ? 'text-accent' : isCompleted ? 'text-ink' : 'text-ink-faint'
                  }`}
                >
                  {step.label}
                </span>
                {index < WIZARD_STEPS.length - 1 && (
                  <ArrowRightIcon className="w-4 h-4 mx-3 text-ink-faint" />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="bg-surface rounded-button shadow-sm p-6">{renderCurrentStep()}</div>
    </div>
  )
}
